// src/services/clientManager.js
import { db } from './firebase';
import { sirvoyDb } from './firebaseConfig';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  addDoc,
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { 
  getSirvoyClientByEmail, 
  getSirvoyClientStats, 
  getSirvoyClientHistoryByEmail 
} from './sirvoyArchives';

// Normalise une chaîne pour la comparaison (minuscules, sans accents, sans espaces)
export function normalizeString(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retire les accents
    .replace(/[^a-z0-9]/g, ''); // Garde que lettres et chiffres
}

// Génère un numéro de client unique
async function generateClientNumber() {
  const clientsRef = collection(db, 'clients');
  const snapshot = await getDocs(clientsRef);
  const maxNumber = snapshot.docs.reduce((max, doc) => {
    const num = parseInt(doc.data().clientNumber || '0');
    return num > max ? num : max;
  }, 0);
  return String(maxNumber + 1).padStart(6, '0'); // Format: 000001, 000002, etc.
}

// Génère un numéro de membre du club unique
async function generateClubMemberNumber() {
  const clientsRef = collection(db, 'clients');
  const q = query(clientsRef, where('isClubMember', '==', true));
  const snapshot = await getDocs(q);
  const maxNumber = snapshot.docs.reduce((max, doc) => {
    const num = parseInt(doc.data().clubMemberNumber || '0');
    return num > max ? num : max;
  }, 0);
  return String(maxNumber + 1).padStart(4, '0'); // Format: 0001, 0002, etc.
}

// Cherche un client existant selon les critères de matching
export async function findExistingClient(clientData) {
  const clientsRef = collection(db, 'clients');
  
  // Normaliser les données de recherche
  const normalizedEmail = normalizeString(clientData.email);
  const normalizedPhone = normalizeString(clientData.phone);
  const normalizedName = normalizeString(clientData.name);
  const normalizedFirstName = normalizeString(clientData.firstName);
  
  // 1. Recherche par email (le plus fiable)
  if (normalizedEmail) {
    const emailQuery = query(
      clientsRef, 
      where('searchKeys.normalizedEmail', '==', normalizedEmail)
    );
    const emailSnapshot = await getDocs(emailQuery);
    if (!emailSnapshot.empty) {
      return { id: emailSnapshot.docs[0].id, ...emailSnapshot.docs[0].data() };
    }
  }
  
  // 2. Recherche par téléphone
  if (normalizedPhone) {
    const phoneQuery = query(
      clientsRef, 
      where('searchKeys.normalizedPhone', '==', normalizedPhone)
    );
    const phoneSnapshot = await getDocs(phoneQuery);
    if (!phoneSnapshot.empty) {
      return { id: phoneSnapshot.docs[0].id, ...phoneSnapshot.docs[0].data() };
    }
  }
  
  // 3. Recherche par nom ET prénom
  if (normalizedName && normalizedFirstName) {
    const nameQuery = query(
      clientsRef,
      where('searchKeys.normalizedName', '==', normalizedName),
      where('searchKeys.normalizedFirstName', '==', normalizedFirstName)
    );
    const nameSnapshot = await getDocs(nameQuery);
    if (!nameSnapshot.empty) {
      return { id: nameSnapshot.docs[0].id, ...nameSnapshot.docs[0].data() };
    }
  }
  
  return null; // Aucun client trouvé
}

// Crée un nouveau client (avec intégration historique Sirvoy si existe)
export async function createClient(clientData) {
  const clientNumber = await generateClientNumber();
  const newClientRef = doc(collection(db, 'clients'));
  
  // Chercher si ce client existe dans les archives Sirvoy (par email)
  let sirvoyHistory = null;
  let sirvoyClientData = null;
  
  if (clientData.email) {
    try {
      sirvoyClientData = await getSirvoyClientByEmail(clientData.email);
      
      if (sirvoyClientData) {
        // Récupérer les stats Sirvoy
        const sirvoyStats = await getSirvoyClientStats(clientData.email);
        
        sirvoyHistory = {
          sirvoyClientNumber: sirvoyClientData.clientNumber || null,
          sirvoyClientId: sirvoyClientData.id || null,
          totalReservations: sirvoyStats.totalReservations || 0,
          totalCancellations: sirvoyStats.totalCancellations || 0,
          totalSpent: sirvoyStats.totalSpent || 0,
          firstVisit: sirvoyStats.firstVisit || null,
          lastVisit: sirvoyStats.lastVisit || null,
          years: sirvoyStats.years || [],
          migratedAt: Timestamp.now()
        };
        
        console.log(`📦 Historique Sirvoy trouvé pour ${clientData.email}: ${sirvoyHistory.totalReservations} réservations`);
        
        // Marquer le client Sirvoy comme migré
        if (sirvoyClientData.id) {
          try {
            const sirvoyClientRef = doc(sirvoyDb, 'clients', sirvoyClientData.id);
            await updateDoc(sirvoyClientRef, {
              migrated: true,
              migratedAt: Timestamp.now(),
              valetierClientId: newClientRef.id,
              valetierClientNumber: clientNumber
            });
            console.log(`✅ Client Sirvoy ${sirvoyClientData.id} marqué comme migré`);
          } catch (migrationError) {
            console.warn('⚠️ Impossible de marquer le client Sirvoy comme migré:', migrationError);
          }
        }
      }
    } catch (sirvoyError) {
      console.warn('⚠️ Erreur recherche historique Sirvoy:', sirvoyError);
    }
  }
  
  const newClient = {
    clientNumber,
    firstName: clientData.firstName || '',
    name: clientData.name || '',
    company: clientData.company || '',
    address: clientData.address || '',
    city: clientData.city || '',
    postalCode: clientData.postalCode || '',
    country: clientData.country || '',
    phone: clientData.phone || '',
    email: clientData.email || '',
    
    // Métadonnées
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    
    // Statistiques VALETIER (commence à 0)
    totalReservations: 0,
    totalCancellations: 0,
    cancellationCount: 0,
    returnCount: 0,
    lastReservationDate: null,
    firstReservationDate: null,
    isReturningClient: sirvoyHistory ? sirvoyHistory.totalReservations > 0 : false,
    
    // Historique Sirvoy intégré (si existe)
    sirvoyHistory: sirvoyHistory || null,
    
    // Infos personnalisées
    tags: [],
    preferences: '',
    notes: '',
    
    // Membre du club
    isClubMember: false,
    clubMemberNumber: null,
    clubMemberSince: null,
    
    // Clés de recherche normalisées
    searchKeys: {
      normalizedName: normalizeString(clientData.name),
      normalizedFirstName: normalizeString(clientData.firstName),
      normalizedEmail: normalizeString(clientData.email),
      normalizedPhone: normalizeString(clientData.phone)
    }
  };
  
  await setDoc(newClientRef, newClient);
  return { id: newClientRef.id, ...newClient };
}

// Met à jour les infos d'un client depuis une réservation
export async function updateClientFromReservation(clientId, reservationData) {
  const clientRef = doc(db, 'clients', clientId);
  const clientSnap = await getDoc(clientRef);
  
  if (!clientSnap.exists()) return;
  
  const clientData = clientSnap.data();
  const updateData = {
    // Mise à jour des coordonnées (dernière résa fait foi)
    firstName: reservationData.client.firstName || clientData.firstName,
    name: reservationData.client.name || clientData.name,
    company: reservationData.client.company || clientData.company,
    address: reservationData.client.address || clientData.address,
    city: reservationData.client.city || clientData.city,
    postalCode: reservationData.client.postalCode || clientData.postalCode,
    country: reservationData.client.country || clientData.country,
    phone: reservationData.client.phone || clientData.phone,
    email: reservationData.client.email || clientData.email,
    
    // Mise à jour des clés de recherche
    'searchKeys.normalizedName': normalizeString(reservationData.client.name),
    'searchKeys.normalizedFirstName': normalizeString(reservationData.client.firstName),
    'searchKeys.normalizedEmail': normalizeString(reservationData.client.email),
    'searchKeys.normalizedPhone': normalizeString(reservationData.client.phone),
    
    updatedAt: Timestamp.now()
  };
  
  await updateDoc(clientRef, updateData);
}

// Met à jour les statistiques d'un client
export async function updateClientStats(clientId, reservationData) {
  const clientRef = doc(db, 'clients', clientId);
  const clientSnap = await getDoc(clientRef);
  
  if (!clientSnap.exists()) return;
  
  const clientData = clientSnap.data();
  const reservationDate = reservationData.checkIn;
  
  const updateData = {
    totalReservations: (clientData.totalReservations || 0) + 1,
    lastReservationDate: reservationDate,
    firstReservationDate: clientData.firstReservationDate || reservationDate,
    isReturningClient: (clientData.totalReservations || 0) + 1 > 1,
    updatedAt: Timestamp.now()
  };
  
  await updateDoc(clientRef, updateData);
}

// 🆕 NOUVELLE FONCTION : Recalculer les statistiques d'un client
export async function recalculateClientStats(clientId) {
  try {
    const clientRef = doc(db, 'clients', clientId);
    const clientSnap = await getDoc(clientRef);
    
    if (!clientSnap.exists()) {
      console.error('Client introuvable:', clientId);
      return;
    }
    
    // Récupérer toutes les réservations du client
    const reservationsRef = collection(db, 'reservations');
    const q = query(reservationsRef, where('clientId', '==', clientId));
    const snapshot = await getDocs(q);
    
    let totalReservations = 0;
    let totalCancellations = 0;
    let cancellationCount = 0;
    let returnCount = 0;
    let firstReservationDate = null;
    let lastReservationDate = null;
    
    snapshot.forEach(doc => {
      const reservation = doc.data();
      
      if (reservation.status === 'cancelled') {
        totalCancellations++;
        cancellationCount++;
      } else {
        totalReservations++;
        returnCount++;
        
        const checkInDate = reservation.checkIn?.toDate ? reservation.checkIn.toDate() : new Date(reservation.checkIn);
        
        if (!firstReservationDate || checkInDate < firstReservationDate) {
          firstReservationDate = checkInDate;
        }
        
        if (!lastReservationDate || checkInDate > lastReservationDate) {
          lastReservationDate = checkInDate;
        }
      }
    });
    
    // Mettre à jour le client
    await updateDoc(clientRef, {
      totalReservations,
      totalCancellations,
      cancellationCount,
      returnCount,
      firstReservationDate: firstReservationDate ? Timestamp.fromDate(firstReservationDate) : null,
      lastReservationDate: lastReservationDate ? Timestamp.fromDate(lastReservationDate) : null,
      isReturningClient: totalReservations > 1,
      updatedAt: Timestamp.now()
    });
    
    console.log(`✅ Stats recalculées pour client ${clientId}:`, {
      totalReservations,
      totalCancellations,
      cancellationCount,
      returnCount
    });
    
  } catch (error) {
    console.error('Erreur recalcul stats client:', error);
    throw error;
  }
}

// ============================================
// 🆕 GESTION ASYNC : findOrCreateClient()
// ============================================

const clientCreationLocks = new Map();

export async function findOrCreateClient(clientData) {
  const email = clientData.email?.toLowerCase().trim();
  
  // Si un client avec cet email est déjà en cours de création, on attend
  if (email && clientCreationLocks.has(email)) {
    console.log('⏳ Création client en cours, attente...');
    return await clientCreationLocks.get(email);
  }
  
  const creationPromise = (async () => {
    try {
      const existingClient = await findExistingClient(clientData);
      if (existingClient) {
        console.log('✅ Client existant trouvé:', existingClient.id);
        return existingClient;
      }
      
      console.log('🆕 Création nouveau client...');
      const newClient = await createClient(clientData);
      return newClient;
    } finally {
      if (email) {
        clientCreationLocks.delete(email);
      }
    }
  })();
  
  if (email) {
    clientCreationLocks.set(email, creationPromise);
  }
  
  return await creationPromise;
}



// Récupère toutes les réservations d'un client
export async function getClientReservations(clientId) {
  const reservationsRef = collection(db, 'reservations');
  const q = query(
    reservationsRef,
    where('clientId', '==', clientId),
    orderBy('checkIn', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Récupère tous les clients
export async function getAllClients() {
  const clientsRef = collection(db, 'clients');
  const q = query(clientsRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Active le statut membre du club
export async function activateClubMembership(clientId) {
  const clientRef = doc(db, 'clients', clientId);
  const clientSnap = await getDoc(clientRef);
  
  if (!clientSnap.exists()) return;
  
  const clientData = clientSnap.data();
  
  // Si déjà membre, ne rien faire
  if (clientData.isClubMember) return clientData;
  
  const clubMemberNumber = await generateClubMemberNumber();
  
  await updateDoc(clientRef, {
    isClubMember: true,
    clubMemberNumber,
    clubMemberSince: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  
  return { ...clientData, isClubMember: true, clubMemberNumber };
}

// Désactive le statut membre du club (le numéro ne sera pas réutilisé)
export async function deactivateClubMembership(clientId) {
  const clientRef = doc(db, 'clients', clientId);
  const clientSnap = await getDoc(clientRef);
  
  if (!clientSnap.exists()) return;
  
  const clientData = clientSnap.data();
  
  // Garder une trace de l'ancien numéro dans l'historique
  await updateDoc(clientRef, {
    isClubMember: false,
    formerClubMemberNumber: clientData.clubMemberNumber, // Archive l'ancien numéro
    clubMemberNumber: null,
    clubMemberSince: null,
    clubMemberRemovedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  
  return { ...clientData, isClubMember: false };
}

// Met à jour les tags d'un client
export async function updateClientTags(clientId, tags) {
  const clientRef = doc(db, 'clients', clientId);
  await updateDoc(clientRef, {
    tags,
    updatedAt: Timestamp.now()
  });
}

// Met à jour les préférences et notes d'un client
export async function updateClientPreferences(clientId, preferences, notes) {
  const clientRef = doc(db, 'clients', clientId);
  await updateDoc(clientRef, {
    preferences,
    notes,
    updatedAt: Timestamp.now()
  });
}

// ============================================
// 🆕 NOUVELLES FONCTIONS POUR CHANGEMENT CLIENT
// ============================================

/**
 * Recherche de clients
 */
export const searchClients = async (query) => {
  try {
    const clientsRef = collection(db, 'clients');
    const snapshot = await getDocs(clientsRef);
    
    const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    const results = [];
    snapshot.forEach(doc => {
      const client = { id: doc.id, ...doc.data() };
      
      const firstName = (client.firstName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const name = (client.name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const email = (client.email || '').toLowerCase();
      
      if (firstName.includes(normalizedQuery) || 
          name.includes(normalizedQuery) || 
          email.includes(normalizedQuery)) {
        results.push(client);
      }
    });
    
    // Trier par pertinence (exact match en premier)
    results.sort((a, b) => {
      const aFullName = `${a.firstName} ${a.name}`.toLowerCase();
      const bFullName = `${b.firstName} ${b.name}`.toLowerCase();
      
      if (aFullName === normalizedQuery) return -1;
      if (bFullName === normalizedQuery) return 1;
      
      return aFullName.localeCompare(bFullName);
    });
    
    return results.slice(0, 10); // Max 10 résultats
  } catch (error) {
    console.error('Erreur recherche clients:', error);
    return [];
  }
};

/**
 * Détache une réservation d'un client
 */
export const unlinkReservationFromClient = async (reservationId, oldClientId) => {
  try {
    if (!oldClientId) return;
    
    // Recalculer les stats du client après détachement
    await recalculateClientStats(oldClientId);
  } catch (error) {
    console.error('Erreur détachement réservation:', error);
    throw error;
  }
};

/**
 * Lie une réservation à un client
 */
export const linkReservationToClient = async (reservationId, newClientId, reservationData) => {
  try {
    // 1. Mettre à jour la réservation
    await updateDoc(doc(db, 'reservations', reservationId), {
      clientId: newClientId,
      modifiedAt: Timestamp.now()
    });
    
    // 2. Recalculer les stats du nouveau client
    await recalculateClientStats(newClientId);
    
    // 3. Mettre à jour les infos de contact depuis la réservation
    const clientRef = doc(db, 'clients', newClientId);
    const clientSnap = await getDoc(clientRef);
    
    if (clientSnap.exists()) {
      const clientData = clientSnap.data();
      
      await updateDoc(clientRef, {
        phone: reservationData.client.phone || clientData.phone,
        email: reservationData.client.email || clientData.email,
        address: reservationData.client.address || clientData.address,
        city: reservationData.client.city || clientData.city,
        postalCode: reservationData.client.postalCode || clientData.postalCode,
        country: reservationData.client.country || clientData.country,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Erreur liaison réservation:', error);
    throw error;
  }
};

/**
 * Crée un nouveau client à partir d'une réservation
 */
export const createClientFromReservation = async (reservation) => {
  try {
    const clientNumber = await generateClientNumber();
    
    const clientData = {
      clientNumber,
      firstName: reservation.client.firstName || '',
      name: reservation.client.name || '',
      email: reservation.client.email || '',
      phone: reservation.client.phone || '',
      address: reservation.client.address || '',
      city: reservation.client.city || '',
      postalCode: reservation.client.postalCode || '',
      country: reservation.client.country || '',
      company: reservation.client.company || '',
      totalReservations: 1,
      totalCancellations: 0,
      cancellationCount: 0,
      returnCount: 1,
      lastReservationDate: reservation.checkIn,
      firstReservationDate: reservation.checkIn,
      isReturningClient: false,
      tags: [],
      preferences: '',
      notes: '',
      isClubMember: false,
      clubMemberNumber: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      searchKeys: {
        normalizedName: normalizeString(reservation.client.name),
        normalizedFirstName: normalizeString(reservation.client.firstName),
        normalizedEmail: normalizeString(reservation.client.email),
        normalizedPhone: normalizeString(reservation.client.phone)
      }
    };
    
    // Créer le nouveau client
    const clientRef = await addDoc(collection(db, 'clients'), clientData);
    
    // Mettre à jour la réservation avec le nouveau clientId
    await updateDoc(doc(db, 'reservations', reservation.id), {
      clientId: clientRef.id,
      modifiedAt: Timestamp.now()
    });
    
    return clientRef.id;
  } catch (error) {
    console.error('Erreur création client:', error);
    throw error;
  }
};